import { useMemo, useState } from "react";
import { Building2, CheckCircle2, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { ErrorCard, EmptyCard, LoadingBlock, PageHeader, Pill } from "../components/jamora-admin/AdminBits";
import { FilterTabs } from "../components/jamora-admin/ListBits";
import useAdminFetch from "../hooks/useAdminFetch";
import {
  OVERVIEW_SECTIONS, TRAINING_FIGURES, comparableFigures, formatCount, formatFigure, formatFcfa, pharmacyPath, pilotSummary, rankRows, watchItems,
} from "../lib/jamoraAdmin";
import { canViewPharmacy } from "../lib/jamoraAdmin";
import { useAuth } from "../contexts/AuthContext";
import { canSeeTickets, formatDateTime } from "../lib/tickets";
import { canOpenAssist } from "../lib/assist";

const STATUS_TONES = { "Migration incomplète": "amber", "OnePharma + Jamora": "sky", "Onboarding à faire": "amber", "Sans activité récente": "slate", "En utilisation": "emerald" };
const DOT = { red: "bg-red-500", amber: "bg-amber-500", slate: "bg-slate-400", emerald: "bg-emerald-500" };

// The six figures a person wants first, each with one line of context. A figure Jamora did not send shows « — », never an invented zero.
const KPIS = [
  { id: "ventes_today", label: "Ventes aujourd'hui", value: (c) => formatFigure(c.ventes?.global?.ca_today, "fcfa"), sub: (c) => `${formatFigure(c.ventes?.global?.ca_30j, "fcfa")} sur 30 jours · ${formatFigure(c.ventes?.global?.unites_today, "count")} boîtes aujourd'hui` },
  { id: "pharmacies", label: "Pharmacies actives", value: (c) => formatCount(c.registry_global?.nb_pharmacies), sub: (c) => `${formatCount(c.registry_global?.nb_onboarding_complete)} avec onboarding terminé` },
  { id: "ventes_count", label: "Ventes sur 30 jours", value: (c) => formatCount(c.ventes?.global?.nb_ventes_30j), sub: (c) => `${formatFigure(c.ventes?.global?.unites_30j, "count")} boîtes · panier moyen ${formatFigure(c.ventes?.global?.panier_moyen, "fcfa")}` },
  { id: "ruptures", label: "Produits en rupture", value: (c) => formatCount(c.stock?.global?.nb_ruptures), sub: (c) => `${formatCount(c.stock?.global?.nb_stock_bas)} en stock bas` },
  { id: "dette", label: "Dette clients", value: (c) => formatFigure(c.clients_recouvrement?.global?.dette_en_cours, "fcfa"), sub: (c) => `${formatCount(c.clients_recouvrement?.global?.nb_debiteurs)} débiteurs` },
  { id: "expires", label: "Médicaments périmés", value: (c) => formatCount(c.medicaments?.global?.nb_expires), sub: (c) => `${formatCount(c.medicaments?.global?.nb_expire_30j)} expirent sous 30 jours` },
];

// « 125 000 FCFA » is read as a number first: the currency goes small beside it so the figure never wraps on two lines.
function Kpi({ kpi, content }) {
  const value = String(kpi.value(content));
  const money = value.endsWith(" FCFA");
  return (
    <Card className="p-4" data-testid={`kpi-${kpi.id}`}>
      <p className="text-xs font-medium text-slate-500">{kpi.label}</p>
      <p className="mt-1 text-xl font-semibold text-slate-900 tabular-nums whitespace-nowrap" data-testid={`kpi-${kpi.id}-value`}>
        {money ? value.slice(0, -5) : value}{money && <span className="ml-1 text-xs font-medium text-slate-500">FCFA</span>}
      </p>
      <p className="mt-1 text-xs text-slate-500">{kpi.sub(content)}</p>
    </Card>
  );
}

// A thin bar scaled to the best pharmacy of the list; purely visual, the figure beside it carries the information.
function Bar({ ratio, tone = "bg-jamora" }) {
  return <div className="h-2 w-full rounded-full bg-slate-100" aria-hidden="true"><div className={`h-2 rounded-full ${tone}`} style={{ width: `${Math.round(ratio * 100)}%` }} /></div>;
}

function Watch({ content, canAssist, canTickets }) {
  const items = useMemo(() => watchItems(content), [content]);
  return (
    <Card className="p-4 space-y-3" data-testid="overview-watch">
      <h2 className="text-sm font-semibold text-slate-900">À surveiller</h2>
      {!items.length && (
        <p className="flex items-center gap-2 text-sm text-slate-600" data-testid="watch-empty"><CheckCircle2 size={16} className="text-emerald-600" />Rien à signaler pour l'instant.</p>
      )}
      <ul className="divide-y divide-slate-100">
        {items.map((item) => (
          <li key={item.id} className="py-2.5 flex items-start gap-3" data-testid={`watch-${item.id}`}>
            <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${DOT[item.tone] || DOT.slate}`} aria-hidden="true" />
            <div className="min-w-0 text-sm">
              <p className="text-slate-900"><span className="font-semibold tabular-nums">{formatCount(item.count)}</span> {item.label}</p>
              {item.detail && <p className="text-xs text-slate-500">{item.detail}</p>}
              {item.pharmacies && (
                <p className="text-xs text-slate-500 break-words">
                  {item.pharmacies.slice(0, 4).map((p, index) => (
                    <span key={p.schema}>{index > 0 && ", "}{p.schema ? <Link className="text-jamora hover:underline" to={pharmacyPath(p.schema)}>{p.name}</Link> : p.name}</span>
                  ))}
                  {item.pharmacies.length > 4 && ` et ${item.pharmacies.length - 4} autre(s)`}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
      {(canAssist || canTickets) && (
        <p className="border-t border-slate-100 pt-3 text-xs text-slate-500" data-testid="overview-links">
          Demandes d'aide des pharmacies : {canAssist && <Link className="text-jamora hover:underline" to="/assistance">Assistance à distance</Link>}{canAssist && canTickets && " · "}{canTickets && <Link className="text-jamora hover:underline" to="/support/tickets">Tickets support</Link>}
        </p>
      )}
    </Card>
  );
}

function Pilots({ content, canOpenPharmacy }) {
  const pilots = content.pilotes || [];
  const summary = pilotSummary(pilots);
  const best = Math.max(0, ...pilots.map((p) => Number(p.nb_ventes_natives_30j) || 0));
  const anyLogin = pilots.some((p) => p.derniere_connexion);
  const nameOf = (p) => (canOpenPharmacy ? <Link className="text-jamora hover:underline" to={pharmacyPath(p.schema_name)}>{p.pharmacie}</Link> : p.pharmacie);
  return (
    <Card className="p-4 space-y-3" data-testid="overview-pilots">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <h2 className="text-sm font-semibold text-slate-900">Pharmacies pilotes</h2>
        <span className="text-sm text-slate-600" data-testid="overview-count">{formatCount(content.registry_global.nb_pharmacies)} pharmacie{content.registry_global.nb_pharmacies > 1 ? "s" : ""} · {formatCount(content.registry_global.nb_utilisateurs_total)} utilisateurs</span>
        <div className="flex flex-wrap gap-1.5">{summary.map((s) => <Pill key={s.label} tone={STATUS_TONES[s.label] || "slate"} testid={`pilot-summary-${s.label}`}>{s.count} · {s.label}</Pill>)}</div>
      </div>
      <div className="hidden md:block overflow-auto max-h-[32rem]">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-100 sticky top-0 bg-white">
              <th className="py-2 pr-3 font-medium">Pharmacie</th><th className="py-2 pr-3 font-medium">Statut</th>
              <th className="py-2 pr-3 font-medium w-56">Ventes Jamora (30 j)</th><th className="py-2 pr-3 font-medium text-right">Jours actifs</th>
              <th className="py-2 pr-3 font-medium hidden 2xl:table-cell">Modules utilisés</th>{anyLogin && <th className="py-2 font-medium hidden lg:table-cell">Dernière connexion</th>}
            </tr>
          </thead>
          <tbody>
            {pilots.map((p) => (
              <tr key={p.schema_name} className="border-b border-slate-50 last:border-0" data-testid={`pilot-row-${p.schema_name}`}>
                <td className="py-2.5 pr-3 font-medium text-slate-900">{nameOf(p)}</td>
                <td className="py-2.5 pr-3"><Pill tone={STATUS_TONES[p.statut] || "slate"}>{p.statut}</Pill></td>
                <td className="py-2.5 pr-3">
                  <div className="flex items-center gap-3"><span className="w-14 text-right tabular-nums shrink-0">{formatCount(p.nb_ventes_natives_30j)}</span><Bar ratio={best ? (Number(p.nb_ventes_natives_30j) || 0) / best : 0} /></div>
                </td>
                <td className="py-2.5 pr-3 text-right tabular-nums">{formatCount(p.jours_actifs_30j)}</td>
                <td className="py-2.5 pr-3 hidden 2xl:table-cell text-slate-600">{(p.modules_utilises_30j || []).join(", ") || "—"}</td>
                {anyLogin && <td className="py-2.5 hidden lg:table-cell whitespace-nowrap">{p.derniere_connexion ? formatDateTime(p.derniere_connexion) : "—"}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="md:hidden divide-y divide-slate-100 max-h-[32rem] overflow-y-auto" data-testid="pilot-cards">
        {pilots.map((p) => (
          <li key={p.schema_name} className="py-3 space-y-1.5">
            <div className="flex items-center justify-between gap-2"><span className="font-medium text-slate-900">{nameOf(p)}</span><Pill tone={STATUS_TONES[p.statut] || "slate"}>{p.statut}</Pill></div>
            <div className="flex items-center gap-3 text-xs text-slate-500"><span className="shrink-0">{formatCount(p.nb_ventes_natives_30j)} ventes · {formatCount(p.jours_actifs_30j)} j actifs</span><Bar ratio={best ? (Number(p.nb_ventes_natives_30j) || 0) / best : 0} /></div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// One business area at a time: its figures for the whole platform, then the pharmacies compared on the figure picked.
function Sections({ content }) {
  const [sectionId, setSectionId] = useState(OVERVIEW_SECTIONS[0].id);
  const [metricKey, setMetricKey] = useState({});
  const section = OVERVIEW_SECTIONS.find((s) => s.id === sectionId);
  const block = content[section.id] || {};
  const global = block.global || {};
  const comparable = comparableFigures(section);
  const metric = comparable.find(([key]) => key === metricKey[section.id]) || comparable[0];
  const ranked = useMemo(() => (metric ? rankRows(block.rows, metric[0]) : []), [block.rows, metric]);
  const kind = metric?.[2];
  return (
    <Card className="p-4 space-y-4" data-testid="overview-sections">
      <h2 className="text-sm font-semibold text-slate-900">Détail par domaine</h2>
      <FilterTabs label="Domaine" tabs={OVERVIEW_SECTIONS.map((s) => [s.id, s.label])} value={sectionId} onChange={setSectionId} testid="overview-tab" />
      <div data-testid={`overview-${section.id}`} className="space-y-4">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
          {section.figures.map(([key, label, figureKind]) => (
            <div key={key} className="min-w-0">
              <dt className="text-xs text-slate-500">{label}</dt>
              <dd className="text-lg font-semibold text-slate-900 tabular-nums break-words" data-testid={`overview-${section.id}-${key}`}>{formatFigure(global[key], figureKind)}</dd>
            </div>
          ))}
        </dl>
        {ranked.length > 0 && (
          <div data-testid={`overview-${section.id}-compare`}>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-xs font-medium text-slate-500">Comparer les pharmacies sur :</span>
              {comparable.map(([key, label]) => (
                <button key={key} type="button" aria-pressed={metric[0] === key} onClick={() => setMetricKey((m) => ({ ...m, [section.id]: key }))}
                  className={`h-7 rounded-full border px-3 text-xs ${metric[0] === key ? "border-jamora bg-jamora text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>{label}</button>
              ))}
            </div>
            <ul className="space-y-2" data-testid="overview-bars">
              {ranked.map((row) => (
                <li key={row.name} className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)_minmax(0,8rem)] sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_minmax(0,9rem)] items-center gap-3 text-sm">
                  <span className="truncate text-slate-900" title={row.name}>{row.name}</span>
                  <Bar ratio={row.ratio} />
                  <span className="text-right tabular-nums text-slate-900">{kind === "fcfa" ? formatFcfa(row.value) : formatFigure(row.value, kind)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}

// The platform's training usage, small: counters only, over the period Jamora computes.
function TrainingCard() {
  const { phase, data } = useAdminFetch("/training-stats");
  if (phase !== "ready") return null;
  return (
    <Card className="p-4 space-y-3" data-testid="overview-training">
      <h2 className="text-sm font-semibold text-slate-900">Formation — {data.period_days} derniers jours</h2>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-6">
        {TRAINING_FIGURES.map(([key, label]) => (
          <div key={key}><dt className="text-xs text-slate-500">{label}</dt><dd className="text-base font-semibold text-slate-900 tabular-nums" data-testid={`overview-training-${key}`}>{formatCount(data[key])}</dd></div>
        ))}
        <div><dt className="text-xs text-slate-500">Modules jamais consultés</dt><dd className="text-base font-semibold text-slate-900 tabular-nums">{formatCount(data.never_seen_module_count)} sur {formatCount(data.published_module_count)}</dd></div>
      </dl>
      {data.schemas_unavailable.length > 0 && <p className="text-xs text-amber-700">{data.schemas_unavailable.length} pharmacie(s) non lue(s) : chiffres partiels.</p>}
    </Card>
  );
}

export default function JamoraAdminOverview() {
  const { user } = useAuth();
  const canAssist = canOpenAssist(user);
  const canTickets = canSeeTickets(user);
  const { phase, data, error, reload } = useAdminFetch("/overview");
  const canOpenPharmacy = canViewPharmacy(user);
  const content = data?.data;
  const empty = phase === "ready" && !(content.pilotes || []).length && !content.registry.length;

  return (
    <div className="space-y-4" data-testid="admin-overview">
      <PageHeader title="Vue d'ensemble" description="L'activité des pharmacies actives de la plateforme (démonstrations exclues), et ce qui mérite votre attention."
        actions={(
          <div className="flex items-center gap-3">
            {phase === "ready" && <span className="text-xs text-slate-500" data-testid="overview-updated">Calculé le {formatDateTime(data.generated_at)}{data.cached ? " (gardé 5 minutes)" : ""}</span>}
            <Button variant="outline" size="sm" onClick={reload} disabled={phase === "loading"} data-testid="overview-refresh"><RefreshCw size={14} className="mr-1.5" />Actualiser</Button>
          </div>
        )} />
      {phase === "loading" && <LoadingBlock rows={4} />}
      {phase === "error" && <ErrorCard message={error} onRetry={reload} />}
      {empty && <EmptyCard icon={Building2} title="Aucune pharmacie active" hint="Les chiffres apparaîtront dès qu'une pharmacie réelle sera active." testid="overview-empty" />}
      {phase === "ready" && !empty && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="overview-kpis">
            {KPIS.map((kpi) => <Kpi key={kpi.id} kpi={kpi} content={content} />)}
          </div>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
            <Pilots content={content} canOpenPharmacy={canOpenPharmacy} />
            <Watch content={content} canAssist={canAssist} canTickets={canTickets} />
          </div>
          <Sections content={content} />
          <TrainingCard />
        </>
      )}
    </div>
  );
}
